import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, bullhornApi, tenantsApi } from '../services/api';
import { 
  FileText, 
  Send, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  RefreshCw,
  Link2,
  ArrowRight,
  Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

const StatCard = ({ icon: Icon, label, value, color, delay, onClick }) => (
  <div 
    className={`neumorphic rounded-xl p-6 animate-fade-in opacity-0 stagger-${delay} ${onClick ? 'cursor-pointer card-hover transition-transform hover:-translate-y-0.5' : ''}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    data-testid={`stat-${label.toLowerCase().replace(' ', '-')}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="overline mb-2">{label}</p>
        <p className="font-heading text-3xl font-bold text-foreground">{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const statusConfig = {
    sent: { class: 'status-sent', label: 'Sent' },
    opened: { class: 'status-opened', label: 'Opened' },
    in_progress: { class: 'status-opened', label: 'In Progress' },
    completed: { class: 'status-completed', label: 'Completed' },
    expired: { class: 'status-expired', label: 'Expired' },
  };
  
  const config = statusConfig[status] || statusConfig.sent;
  
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.class}`}>
      {config.label}
    </span>
  );
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [bullhornStatus, setBullhornStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const navigate = useNavigate();
  const { tenant, user, getTenantPath } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.is_master_admin;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const statsRes = await dashboardApi.getStats();
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
    checkBullhorn();
  };

  // In the background: Bullhorn can be slow or down, and the dashboard shouldn't wait for it.
  const checkBullhorn = async () => {
    if (tenant?.bullhorn_config?.configured === false) return;
    try {
      const bhStatusRes = await bullhornApi.status();
      setBullhornStatus(bhStatusRes.data);

      // Auto-connect if not connected but tenant has credentials
      if (!bhStatusRes.data?.connected && tenant?.id) {
        try {
          const autoRes = await tenantsApi.autoConnectBullhorn(tenant.id);
          if (autoRes.data.auto_connected) {
            setBullhornStatus({ connected: true });
          }
        } catch (e) {
          // Silent fail — credentials may not exist
        }
      }
    } catch (error) {
      console.warn('Bullhorn status unavailable:', error?.message || error);
    }
  };

  const connectBullhorn = async () => {
    setConnecting(true);
    try {
      await bullhornApi.authenticate();
      setBullhornStatus({ connected: true });
      toast.success('Connected to Bullhorn!');
    } catch (error) {
      toast.error('Failed to connect to Bullhorn');
    } finally {
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground font-body mt-1">
              Overview of your document workflow
            </p>
          </div>
          <div className="flex items-center gap-3">
            {bullhornStatus.connected ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full border border-emerald-200">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-sm font-medium text-emerald-700" data-testid="bullhorn-connected-badge">Bullhorn Connected</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-4 py-2 bg-red-50 rounded-full border border-red-200">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-sm font-medium text-red-700" data-testid="bullhorn-disconnected-badge">Bullhorn Not Connected</span>
                </div>
                <Button 
                  onClick={connectBullhorn} 
                  disabled={connecting}
                  size="sm"
                  data-testid="connect-bullhorn-button"
                >
                  {connecting ? (
                    <><RefreshCw className="w-4 h-4 animate-spin mr-2" />Connecting...</>
                  ) : (
                    <><Link2 className="w-4 h-4 mr-2" />Connect</>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            icon={FileText} 
            label="Total Documents" 
            value={stats?.documents?.total || 0}
            color="bg-gradient-to-br from-[#1e293b] to-[#334155]"
            delay={1}
            onClick={() => navigate(getTenantPath('/documents'))}
          />
          <StatCard 
            icon={Send} 
            label="Sent" 
            value={stats?.documents?.sent || 0}
            color="bg-gradient-to-br from-[#3b82f6] to-[#2563eb]"
            delay={2}
            onClick={() => navigate(getTenantPath('/documents?status=sent'))}
          />
          <StatCard 
            icon={Eye} 
            label="Opened" 
            value={stats?.documents?.opened || 0}
            color="bg-gradient-to-br from-[#f97316] to-[#f59e0b]"
            delay={3}
            onClick={() => navigate(getTenantPath('/documents?status=opened'))}
          />
          <StatCard 
            icon={CheckCircle2} 
            label="Completed" 
            value={stats?.documents?.completed || 0}
            color="bg-gradient-to-br from-[#10b981] to-[#059669]"
            delay={4}
            onClick={() => navigate(getTenantPath('/documents?status=completed'))}
          />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Send Document Card */}
          <div 
            className="neumorphic rounded-xl p-6 card-hover cursor-pointer animate-fade-in opacity-0 stagger-3"
            onClick={() => navigate(getTenantPath('/send'))}
            data-testid="quick-action-send"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center shadow-md shadow-orange-500/20">
                <Send className="w-6 h-6 text-white" />
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground" />
            </div>
            <h3 className="font-heading font-semibold text-lg mb-1">Send Document</h3>
            <p className="text-muted-foreground text-sm font-body">
              Send a document to a candidate for completion
            </p>
          </div>

          {/* Templates Card — admin-only, mirrors the sidebar's Templates tab */}
          {isAdmin && (
            <div
              className="neumorphic rounded-xl p-6 card-hover cursor-pointer animate-fade-in opacity-0 stagger-4"
              onClick={() => navigate(getTenantPath('/templates'))}
              data-testid="quick-action-templates"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1e293b] to-[#334155] flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-1">Manage Templates</h3>
              <p className="text-muted-foreground text-sm font-body">
                Create and edit document templates
              </p>
            </div>
          )}

          {/* Sync Status Card */}
          <div className="neumorphic rounded-xl p-6 animate-fade-in opacity-0 stagger-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3b82f6] to-[#2563eb] flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Bullhorn Sync</h3>
                <p className="text-muted-foreground text-sm font-body">Sync status</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Pending</span>
                <span className="font-medium">{stats?.sync?.pending || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Success</span>
                <span className="font-medium text-emerald-600">{stats?.sync?.success || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Failed</span>
                <span className="font-medium text-red-600">{stats?.sync?.failed || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="neumorphic rounded-xl p-6 animate-fade-in opacity-0 stagger-5">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading font-semibold text-lg">Recent Activity</h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(getTenantPath('/documents'))}
              className="text-primary"
              data-testid="view-all-activity"
            >
              View all
            </Button>
          </div>
          
          {stats?.recent_activity?.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_activity.map((doc, index) => (
                <div 
                  key={doc.id} 
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors duration-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-clay flex items-center justify-center">
                      <span className="font-heading font-semibold text-primary">
                        {doc.candidate_name?.charAt(0)?.toUpperCase() || 'C'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{doc.candidate_name}</p>
                      <p className="text-sm text-muted-foreground">{doc.template_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <StatusBadge status={doc.status} />
                    <span className="text-sm text-muted-foreground">
                      {doc.sent_at && format(new Date(doc.sent_at), 'MMM d, h:mm a')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <img 
                src="https://images.pexels.com/photos/6325886/pexels-photo-6325886.jpeg?auto=compress&cs=tinysrgb&w=300" 
                alt="No documents" 
                className="w-32 h-32 mx-auto mb-4 rounded-xl object-cover opacity-60"
              />
              <p className="text-muted-foreground font-body">No recent activity</p>
              <Button 
                onClick={() => navigate(getTenantPath('/send'))} 
                className="btn-primary mt-4"
                data-testid="send-first-document"
              >
                Send your first document
              </Button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
